<template>
  <ArticlePage :article="article" />
</template>

<script setup>
import { useJournalStore } from '~/stores/journal';

const { $cmsApi } = useNuxtApp();
const route = useRoute();
const config = useRuntimeConfig();
const journalStore = useJournalStore();

const { data, error } = await useAsyncData(async () => {
  let categories = journalStore.categories;

  if (!categories.length) {
    categories = await $cmsApi.get('/articles/categories');
    journalStore.setCategories(categories);
  }

  const url = `/articles/${route.params.slug}${route.query.preview ? `?preview=${route.query.preview}` : ''}`;
  const article = await $cmsApi.get(url);

  return { article };
});

if (error.value) {
  const errorData =
    error.value?.statusCode === 410
      ? { statusCode: 410, message: 'Page Gone' }
      : { statusCode: 404, message: 'Page not found' };
  throw createError(errorData);
}

const article = data.value?.article || {};
const metaData = article?.meta_data || {};

useHead({
  title: article.title_tag || metaData?.title || article.title,
  meta: [
    {
      key: 'description',
      name: 'description',
      content: metaData?.description || article.description,
    },
    {
      key: 'og:title',
      property: 'og:title',
      content: metaData?.title || article.title,
    },
    {
      key: 'og:description',
      property: 'og:description',
      content: metaData?.description || article.description,
    },
    {
      key: 'og:url',
      property: 'og:url',
      content: `${config.public.hostUrl}${route.path}`,
    },
    { key: 'og:image', property: 'og:image', content: article?.image },
    { key: 'og:type', property: 'og:type', content: 'article' },
    ...(metaData?.keywords?.length
      ? [
          {
            key: 'keywords',
            name: 'keywords',
            content: metaData.keywords,
          },
        ]
      : []),
  ].filter((x) => x.content),
  ...(article.json_ld
    ? {}
    : {
        script: [
          {
            key: 'news-article',
            type: 'application/ld+json',
            innerHTML: article.json_ld,
          },
        ],
      }),
});

onMounted(() => {
  const cookieSettings = {
    path: '/',
    maxAge: 60 * 60 * 24 * 365,
  };
  const articlesCookies = useCookie('articles', cookieSettings);
  const valueCookie = useCookie('dealValue', cookieSettings);

  articlesCookies.value = articlesCookies.value ?? [];
  articlesCookies.value.push({ id: article.id });

  if (!valueCookie.value && article.deal_value) {
    valueCookie.value = article.deal_value;
  }
});
</script>
